import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography, useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FolderZipRoundedIcon from '@mui/icons-material/FolderZipRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import dayjs, { Dayjs } from 'dayjs';
import { IResumoNotasCompra, NotasCompraService } from '../../../shared/services/api/Fiscal/NotasCompraService';

// Intervalo entre as consultas: em sequência rápida a SEFAZ bloqueia por consumo indevido.
const PAUSA_ENTRE_CONSULTAS_MS = 300;
// Falhas seguidas indicam SEFAZ/serviço fora do ar: insistir nota a nota só
// acumula timeouts e pode contar como consumo indevido.
const MAX_FALHAS_SEGUIDAS = 3;

interface IDialogVerificarNotasCompraProps {
  open: boolean;
  // Mês em que abre (AAAA-MM): o selecionado no filtro da listagem.
  mesInicial: string;
  onClose: () => void;
  // Situações mudaram: a listagem precisa recarregar.
  onVerificou: () => void;
}

export const DialogVerificarNotasCompra: React.FC<IDialogVerificarNotasCompraProps> = ({ open, mesInicial, onClose, onVerificou }) => {
  const theme = useTheme();
  const [mes, setMes] = useState(mesInicial);
  const [resumo, setResumo] = useState<IResumoNotasCompra | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [progresso, setProgresso] = useState<{ feitas: number; total: number; falhas: number } | null>(null);
  const [baixando, setBaixando] = useState(false);
  const [aviso, setAviso] = useState<{ texto: string; tipo: 'error' | 'warning' | 'success' } | null>(null);
  // Fechar o diálogo (ou "Parar") interrompe a rodada; o que já foi consultado fica salvo.
  const parar = useRef(false);

  const carregarResumo = async (mesAlvo: string) => {
    setCarregando(true);
    const result = await NotasCompraService.resumoDoMes(mesAlvo);
    setCarregando(false);
    if (result instanceof Error) {
      setResumo(null);
      setAviso({ texto: result.message, tipo: 'error' });
      return;
    }
    setResumo(result);
  };

  useEffect(() => {
    if (!open) return;
    setMes(mesInicial);
    setAviso(null);
    setProgresso(null);
    carregarResumo(mesInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mesInicial]);

  const mudarMes = (data: Dayjs | null) => {
    if (!data || !data.isValid()) return;
    const novo = data.format('YYYY-MM');
    setMes(novo);
    setAviso(null);
    carregarResumo(novo);
  };

  const verificar = async () => {
    const pendentes = resumo?.pendentes || [];
    parar.current = false;
    setAviso(null);
    let falhas = 0;
    let falhasSeguidas = 0;
    let feitas = 0;
    let bloqueio = '';
    setProgresso({ feitas, total: pendentes.length, falhas });
    for (const chave of pendentes) {
      if (parar.current) break;
      const result = await NotasCompraService.consultarSituacao(chave);
      feitas += 1;
      if (result instanceof Error) {
        // Bloqueio/serviço fora do ar vale pra todas: insistir só agrava.
        if (result.name === 'BloqueioSefaz') {
          bloqueio = result.message;
          break;
        }
        falhas += 1;
        falhasSeguidas += 1;
        if (falhasSeguidas >= MAX_FALHAS_SEGUIDAS) {
          bloqueio = 'A SEFAZ não respondeu a várias consultas seguidas.';
          break;
        }
      } else {
        falhasSeguidas = 0;
      }
      setProgresso({ feitas, total: pendentes.length, falhas });
      await new Promise((r) => setTimeout(r, PAUSA_ENTRE_CONSULTAS_MS));
    }
    setProgresso(null);
    if (bloqueio) {
      setAviso({ texto: `${bloqueio} A verificação parou; as notas já consultadas foram salvas. Tente de novo mais tarde.`, tipo: 'error' });
    } else if (parar.current) {
      setAviso({ texto: 'Verificação interrompida. As notas já consultadas foram salvas.', tipo: 'warning' });
    } else if (falhas) {
      setAviso({ texto: `${falhas} nota${falhas > 1 ? 's' : ''} não ${falhas > 1 ? 'puderam' : 'pôde'} ser consultada${falhas > 1 ? 's' : ''} agora e ${falhas > 1 ? 'seguem' : 'segue'} como não verificada${falhas > 1 ? 's' : ''}.`, tipo: 'warning' });
    } else {
      setAviso({ texto: 'Verificação concluída.', tipo: 'success' });
    }
    onVerificou();
    await carregarResumo(mes);
  };

  const baixarValidas = async () => {
    setBaixando(true);
    const erro = await NotasCompraService.baixarValidasDoMes(mes);
    setBaixando(false);
    if (erro) setAviso({ texto: erro.message, tipo: 'error' });
  };

  const fechar = () => {
    parar.current = true;
    onClose();
  };

  const verificando = !!progresso;
  const pendentes = resumo?.pendentes.length ?? 0;
  const validasSemXml = resumo ? resumo.normais - resumo.validascomxml : 0;

  const indicador = (rotulo: string, valor: number, cor: string) => (
    <Box key={rotulo} sx={{ p: 1.25, border: `1px solid ${theme.torq.border.default}`, borderRadius: '10px', minWidth: 0 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 750, lineHeight: 1.2, color: cor }}>{valor}</Typography>
      <Typography noWrap variant="caption" color="text.secondary">{rotulo}</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={verificando ? undefined : fechar} maxWidth="sm" fullWidth>
      <DialogTitle>Verificar notas de compra</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker<Dayjs>
              label="Mês de emissão"
              views={['year', 'month']}
              openTo="month"
              format="MM/YYYY"
              disabled={verificando}
              value={dayjs(`${mes}-01`)}
              onChange={mudarMes}
              slotProps={{ textField: { size: 'small', sx: { width: 200 } } }}
            />
          </LocalizationProvider>

          {carregando && !resumo && <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>}

          {resumo && (
            <>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  {resumo.total} nota{resumo.total !== 1 ? 's' : ''} de compra em {dayjs(`${mes}-01`).format('MM/YYYY')}
                </Typography>
                {resumo.normais > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Valor das válidas: {resumo.valorvalidas.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' } }}>
                {indicador('Válidas', resumo.normais, theme.palette.success.dark)}
                {indicador('Canceladas', resumo.canceladas, theme.palette.error.dark)}
                {indicador('Com problema', resumo.denegadas + resumo.irregulares, theme.palette.warning.dark)}
                {indicador('Não verificadas', resumo.naoverificadas, theme.palette.text.secondary)}
              </Box>
            </>
          )}

          {progresso && (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Consultando na SEFAZ: {progresso.feitas} de {progresso.total}</Typography>
              <LinearProgress variant="determinate" value={progresso.total ? (progresso.feitas / progresso.total) * 100 : 0} />
            </Box>
          )}

          {aviso && <Alert severity={aviso.tipo}>{aviso.texto}</Alert>}

          {resumo && !verificando && resumo.total > 0 && pendentes > 0 && (
            <Typography variant="body2" color="text.secondary">
              {pendentes} nota{pendentes !== 1 ? 's' : ''} para consultar. Só entram no download as notas verificadas como válidas.
            </Typography>
          )}
          {!!resumo?.foradoprazo && !verificando && (
            <Alert severity="info">
              {resumo.foradoprazo} nota{resumo.foradoprazo !== 1 ? 's' : ''} com mais de 180 dias da emissão: a consulta de situação tem prazo limitado, então {resumo.foradoprazo !== 1 ? 'não são consultadas e seguem' : 'não é consultada e segue'} como não verificada{resumo.foradoprazo !== 1 ? 's' : ''}.
            </Alert>
          )}
          {validasSemXml > 0 && !verificando && (
            <Alert severity="info">
              {validasSemXml} nota{validasSemXml !== 1 ? 's' : ''} válida{validasSemXml !== 1 ? 's' : ''} só com resumo: sem o XML completo, não {validasSemXml !== 1 ? 'entram' : 'entra'} no download. Dê a Ciência da Operação em NFE Dist para receber o XML.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={verificando ? () => { parar.current = true; } : fechar}>
          {verificando ? 'Parar' : 'Fechar'}
        </Button>
        <Button
          variant="outlined"
          disabled={verificando || baixando || !resumo?.validascomxml}
          startIcon={baixando ? <CircularProgress size={16} /> : <FolderZipRoundedIcon />}
          onClick={baixarValidas}
        >
          Baixar {resumo?.validascomxml || ''} válida{resumo?.validascomxml === 1 ? '' : 's'}
        </Button>
        <Button
          variant="contained"
          disabled={verificando || carregando || !pendentes}
          startIcon={verificando ? <CircularProgress size={16} color="inherit" /> : <VerifiedRoundedIcon />}
          onClick={verificar}
        >
          {resumo && resumo.total > 0 && !pendentes ? 'Tudo verificado' : 'Verificar notas'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
